'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import { CTAButton } from '@/shadcn/ui/button';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';
import { GROQImage } from '@/sanity/lib/definitions';

const ease = [0.25, 0.1, 0.25, 1] as const;

function InfiniteColumn({
  images,
  direction,
}: {
  images: GROQImage[];
  direction: 'down' | 'up';
}) {
  const doubled = [...images, ...images];
  const isDown = direction === 'down';

  return (
    <div className="relative overflow-hidden h-full">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[6rem] lg:h-[12rem] bg-gradient-to-b from-white to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[6rem] lg:h-[12rem] bg-gradient-to-t from-white to-transparent" />
      <motion.div
        className="flex flex-col gap-[1.5rem] h-auto lg:w-[33rem]"
        animate={{ y: isDown ? ['0%', '-50%'] : ['-50%', '0%'] }}
        transition={{
          duration: images.length * 4,
          ease: 'linear',
          repeat: Infinity,
          repeatType: 'loop',
        }}
      >
        {doubled.map((img, i) => (
          <div key={i} className="w-full overflow-hidden rounded-[0.5rem]">
            <GroqImage
              src={img}
              alt=""
              className="!w-full !h-auto !object-cover"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default function IQManagementSection({
  data,
}: {
  data: PageAboutUsSchema;
}) {
  if (
    !data.managementChip &&
    !data.managementTitle &&
    !data.managementImages?.length
  )
    return null;

  const images = data.managementImages ?? [];
  const leftImages = images.filter((_, i) => i % 2 === 0);
  const rightImages = images.filter((_, i) => i % 2 !== 0);

  return (
    <section className="relative w-full bg-white pb-[8rem] lg:pt-[20rem] lg:pb-[24rem]">
      <div className="mx-auto flex w-[min(141rem,calc(100%-4rem))] lg:w-[min(141rem,calc(100%-8rem))] flex-col gap-[4rem] lg:flex-row lg:items-stretch lg:gap-[12rem] justify-between">
        {/* Mobile only: chip + title at top (centered) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="lg:hidden flex flex-col items-center gap-[2rem] text-center"
        >
          {data.managementChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.managementChip}
            </p>
          )}
          {data.managementTitle && (
            <div className="richtext prose-headings:text-[4.5rem] prose-headings:font-medium prose-headings:leading-[1.15] prose-headings:tracking-[-0.225rem]">
              <PortableText
                value={data.managementTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {/* Images — middle on mobile, right on desktop */}
        {!!images.length && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease }}
            className="flex gap-[1.25rem] lg:gap-[1.5rem] overflow-hidden lg:order-last lg:w-[64rem] h-[64rem] lg:h-[74rem]"
          >
            {leftImages.length > 0 && (
              <InfiniteColumn images={leftImages} direction="down" />
            )}
            {rightImages.length > 0 && (
              <InfiniteColumn images={rightImages} direction="up" />
            )}
          </motion.div>
        )}

        {/* Left column: desktop = chip+title+body+CTA | mobile = body+CTA only */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease, delay: 0.1 }}
          className="flex flex-col gap-[2rem] lg:order-first lg:w-[63.75rem]"
        >
          {/* Desktop only: chip + title */}
          <div className="hidden lg:flex flex-col gap-[2rem]">
            {data.managementChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                {data.managementChip}
              </p>
            )}
            {data.managementTitle && (
              <div className="richtext richtext-start prose-headings:text-[7rem] prose-headings:font-medium prose-headings:leading-[1.1] prose-headings:tracking-[-0.35rem]">
                <PortableText
                  value={data.managementTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {/* Body — always visible */}
          {data.managementBody && (
            <div className="richtext lg:richtext-start prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/70 flex flex-col gap-[3.25rem] lg:gap-[4.12rem]">
              <PortableText
                value={data.managementBody}
                components={richTextComponent}
              />
            </div>
          )}

          {data.managementCTA && (
            <div className="w-full lg:w-fit pt-[2rem]">
              <CTAButton data={data.managementCTA} />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
